import { useState } from 'react';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { compressImage, getBase64 } from 'helper';
import { useQuery } from 'hooks/useQuery';
import { isBase64File } from 'components/FileInput';
import { Confluence } from '../interfaces';
import { parseSection } from '../helpers';
import {
  saveConfluenceDefaultArticle,
  saveConfluenceLessonArticle,
  saveConfluencePollArticle,
  updateConfluenceDefaultArticle,
  updateConfluenceLessonArticle,
  updateConfluencePollArticle,
} from '../api';

function useCreateOrUpdateArticle(
  article?: Confluence.IArticleBase | null,
  parentId?: number | null,
  onCreateOrUpdate?: (article: Confluence.IArticleBase) => void,
) {
  const { enqueueSnackbar } = useSnackbar();

  const params = useQuery();

  // раздел базы знаний, в котором находится пользователь — статья создаётся и остаётся в нём
  const section = parseSection(params.get('section'));

  const [{ data, error, loading }, setArticle] = useState<{
    loading: boolean;
    error: string | null;
    data: Confluence.IArticleBase | null;
  }>({ loading: false, error: null, data: null });

  async function handleCreateOrUpdateArticle<T extends Confluence.IArticleBase>(
    articleData: T,
    updateArticle: (data: T) => Promise<void>,
    saveArticle: (data: T) => Promise<void>,
  ) {
    try {
      setArticle((prevState) => ({ ...prevState, loading: true }));

      await (article
        ? updateArticle({ ...articleData, section })
        : saveArticle({ ...articleData, parentId, section }));

      if (onCreateOrUpdate) onCreateOrUpdate(articleData);
      setArticle((prevState) => ({ ...prevState, data: articleData }));
    } catch (error) {
      const message = (error as AxiosError).response?.data.message;

      enqueueSnackbar(message, { variant: 'error' });
      setArticle((prevState) => ({ ...prevState, error: message }));
    } finally {
      setArticle((prevState) => ({ ...prevState, loading: false }));
    }
  }

  async function handleCreateOrUpdateDefaultArticle(articleData: Confluence.IDefaultArticle) {
    handleCreateOrUpdateArticle(articleData, updateConfluenceDefaultArticle, saveConfluenceDefaultArticle);
  }

  async function handleCreateOrUpdateLessonArticle({
    template: { area, zone, realEstates, ...template },
    ...articleData
  }: Confluence.ILessonArticle) {
    const photoBefore = await Promise.all(
      (template.photoBefore || []).map(async (file) => {
        if (isBase64File(file)) return file;
        if (file instanceof File) return getBase64(await compressImage(file));
        return file;
      }),
    );
    const photoAfter = await Promise.all(
      (template.photoAfter || []).map(async (file) => {
        if (isBase64File(file)) return file;
        if (file instanceof File) return getBase64(await compressImage(file));
        return file;
      }),
    );

    handleCreateOrUpdateArticle(
      {
        ...articleData,
        template: {
          ...template,
          areaId: area?.areaId,
          areaName: area?.name,
          zoneId: zone?.zoneId,
          zoneName: zone?.zoneName,
          realEstates: realEstates?.map(({ guid, name }) => ({ realEstateGuid: guid, realEstateName: name })),
          photoBefore,
          photoAfter,
        },
      },
      updateConfluenceLessonArticle,
      saveConfluenceLessonArticle,
    );
  }

  async function handleCreateOrUpdatePollArticle(articleData: Confluence.IPollArticle) {
    handleCreateOrUpdateArticle(articleData, updateConfluencePollArticle, saveConfluencePollArticle);
  }

  return {
    article: data,
    error,
    loading,
    handleCreateOrUpdateDefaultArticle,
    handleCreateOrUpdateLessonArticle,
    handleCreateOrUpdatePollArticle,
  };
}

export default useCreateOrUpdateArticle;
